import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { Auth } from '../auth/auth.service';
import { ErrorTracking } from '../tracking';
import { ActionType } from './actions';
import { ApiHttp } from './api-http.service';
import {
  isDefined,
  objectFilterByKeys,
  Path,
  PathList,
  pathListEqual,
  pathToPathList,
  Query,
  RawStoreState,
  Reference,
  ReferenceList,
  referenceToReferenceList,
  sameElements,
  setDiff,
} from './helpers';
import { ListObservable } from './list-observable';
import { ObjectObservable } from './object-observable';
import { RootModelLists, RootModelObjects } from './root.model';
import { WebSocketService } from './websocket';

export interface ListOptions {
  query: QueryObservable;
}

export interface QueryObservable {
  limit?: number | Observable<number>;
  offset?: number | Observable<number>;
}

@Injectable()
export class Datastore {
  constructor(
    private store: Store<RawStoreState>,
    private apiHttp: ApiHttp,
    private auth: Auth,
    private webSocketService: WebSocketService,
    private errorTracking: ErrorTracking,
  ) {}

  list<a extends keyof RootModelLists>(
    key1: a,
    options?: ListOptions,
  ): ListObservable<a, RootModelLists[a][]>;
  list<a extends keyof RootModelLists, b extends keyof RootModelLists[a]>(
    key1: a,
    key2: b | Observable<b>,
    options?: ListOptions,
  ): ListObservable<a, RootModelLists[a][b][]>;
  list<
    a extends keyof RootModelLists,
    b extends keyof RootModelLists[a],
    c extends keyof RootModelLists[a][b]
  >(
    key1: a,
    key2: b | Observable<b>,
    key3: c | Observable<c>,
    options?: ListOptions,
  ): ListObservable<a, RootModelLists[a][b][c][]>;
  list<T extends keyof RootModelLists>(
    arg1: T,
    ...args: any[]
  ): ListObservable<T, any> {
    let optionsQuery: QueryObservable = {};
    if (args.length && args[args.length - 1].query) {
      optionsQuery = args.pop().query;
    }
    const args$ = [arg1, ...args].map(
      key => (key instanceof Observable ? key : Observable.of(key)),
    );
    const pathStream = Observable.combineLatest(args$);

    const queryStream = Observable.combineLatest(
      [
        optionsQuery.limit instanceof Observable
          ? optionsQuery.limit
          : Observable.of(optionsQuery.limit),
        optionsQuery.offset instanceof Observable
          ? optionsQuery.offset
          : Observable.of(optionsQuery.offset),
      ],
      (limit: number, offset: number) => ({ limit, offset }),
    );

    const refStream: Observable<Reference<T>> = Observable.combineLatest(
      [pathStream, queryStream],
      (path: Path<T>, query: Query) => ({ path, query }),
    )
      .do(ref => {
        const action: ActionType<T> = {
          type: 'REQUEST_DATA',
          payload: {
            type: ref.path[0],
            refList: referenceToReferenceList(ref),
          },
        };
        this.store.dispatch(action);
      })
      .shareReplay(1);

    const fetchStream = this.apiHttp.requestStream
      .withLatestFrom(refStream)
      .filter(([fetchReference, originalReference]) =>
        pathListEqual(
          fetchReference.pathList,
          pathToPathList(originalReference.path),
        ),
      )
      .map(([fetchReference, originalReference]) => originalReference);

    const websocketStream = this.webSocketService.fromServerStream.combineLatest(
      refStream,
      (ws, ref) => ref,
    );

    return new ListObservable(
      refStream,
      this.apiHttp,
      this.auth,
      this.store,
      this.errorTracking,
      Observable.merge(refStream, fetchStream, websocketStream)
        .switchMap(({ path }) =>
          this.store.pluck<RawStoreState, RawStoreState[T][string] | undefined>(
            ...path,
          ),
        )
        .combineLatest(queryStream)
        .distinctUntilChanged(
          ([prevData], [currData, currQuery]) =>
            prevData === currData ||
            // TODO: handle different lists
            (prevData !== undefined &&
              currData !== undefined &&
              prevData.lists.default.length === currData.lists.default.length &&
              !currData.lists.default
                .slice(currQuery.offset || 0)
                .some(
                  (key: string) =>
                    prevData.entities[key] === undefined ||
                    currData.entities[key] === undefined ||
                    prevData.entities[key].entity !==
                      currData.entities[key].entity,
                )),
        )
        .map(([data, query]) => {
          if (data && data.lists && data.lists.default) {
            return data.lists.default
              .slice(query.offset || 0)
              .map((id: any) => data.entities[<string>id].entity);
          }
          return [];
        }),
    );
  }

  object<a extends keyof RootModelObjects>(
    key1: a,
  ): ObjectObservable<RootModelObjects[a]>;
  object<a extends keyof RootModelObjects, b extends keyof RootModelObjects[a]>(
    key1: a,
    key2: b | Observable<b>,
  ): ObjectObservable<RootModelObjects[a][b]>;
  object<
    a extends keyof RootModelObjects,
    b extends keyof RootModelObjects[a],
    c extends keyof RootModelObjects[a][b]
  >(
    key1: a,
    key2: b | Observable<b>,
    key3: c | Observable<c>,
  ): ObjectObservable<RootModelObjects[a][b][c]>;
  object<T extends keyof RootModelObjects>(
    arg1: T,
    ...args: any[]
  ): ObjectObservable<any> {
    const args$ = [arg1, ...args].map(
      key => (key instanceof Observable ? key : Observable.of(key)),
    );

    const refStream: Observable<Reference<T>> = Observable.combineLatest(args$)
      .map((path: Path<T>) => ({ path }))
      .switchMap(
        ({ path }) =>
          this.store
            .pluck<RawStoreState, RawStoreState[T][string] | undefined>(...path)
            .take(1),
        (ref, data) => ({ ref, data }),
      )
      .do(({ ref, data }) => {
        // Don't fetch if this was fetched in the last second.
        if (!data || Number(data.timeFetched) + 1000 < Date.now()) {
          const action: ActionType<T> = {
            type: 'REQUEST_DATA',
            payload: {
              type: ref.path[0],
              refList: referenceToReferenceList(ref),
            },
          };
          this.store.dispatch(action);
        }
      })
      .map(({ ref }) => ref)
      .shareReplay(1);

    const fetchStream = this.apiHttp.requestStream
      .withLatestFrom(refStream)
      .filter(([fetchReference, originalReference]) =>
        pathListEqual(
          fetchReference.pathList,
          pathToPathList(originalReference.path),
        ),
      )
      .map(([fetchReference, originalReference]) => originalReference);

    const subscriptionStream =
      arg1 === 'onlineOffline'
        ? this.webSocketService
            .subscribeOnlineOffline(args$[1])
            .combineLatest(refStream, (ss, ref) => ref)
        : refStream;

    const websocketStream = this.webSocketService.fromServerStream.combineLatest(
      refStream,
      (ws, ref) => ref,
    );

    return new ObjectObservable(
      refStream,
      this.apiHttp,
      this.store,
      Observable.merge(
        refStream,
        fetchStream,
        subscriptionStream,
        websocketStream,
      )
        .switchMap(({ path }) =>
          this.store.pluck<RawStoreState, RawStoreState[T][string] | undefined>(
            ...path,
          ),
        )
        .filter(isDefined)
        .map(obj => obj.entity)
        .distinctUntilChanged(),
    );
  }

  /* Returns a list of objects at a location.
   * If an observable is passed in for key2 then only the delta will be
   * refetched from the network.
   */
  objects<
    T extends keyof RootModelObjects,
    P extends keyof RootModelObjects[T]
  >(
    key1: T,
    key2: P[] | Observable<P[]>,
  ): Observable<RootModelObjects[T][P][]> {
    const args$: Observable<string | string[]>[] = [key1, key2].map(
      key => (key instanceof Observable ? key : Observable.of(key)),
    );

    const refStream: Observable<ReferenceList<T>> = Observable.combineLatest(
      args$,
    )
      .startWith([key1, []])
      .map((pathList: PathList<T>) => ({ refList: { pathList } }))
      .pairwise() // Use pairwise and only fetch new things
      .do(([{ refList: refList1 }, { refList: refList2 }]) => {
        const newIds = setDiff(refList2.pathList[1], refList1.pathList[1]);
        if (newIds.length) {
          const type = refList2.pathList[0];
          const action: ActionType<T> = {
            type: 'REQUEST_DATA',
            payload: { type, refList: { pathList: [type, newIds] } },
          };
          this.store.dispatch(action);
        }
      })
      .map(([{ refList: refList1 }, { refList: refList2 }]) => refList2)
      .shareReplay(1);

    const fetchStream = this.apiHttp.requestStream
      .withLatestFrom(refStream)
      .filter(([fetchReference, originalReference]) =>
        pathListEqual(fetchReference.pathList, originalReference.pathList),
      )
      .map(([fetchReference, originalReference]) => originalReference);

    const websocketStream = this.webSocketService.fromServerStream.combineLatest(
      refStream,
      (ws, ref) => ref,
    );

    return Observable.merge(
      refStream,
      fetchStream,
      websocketStream,
    ).switchMap(({ pathList }) =>
      this.store
        .pluck<RawStoreState, RawStoreState[T] | undefined>(pathList[0])
        .filter(isDefined)
        .map(rawEntitiesObject =>
          objectFilterByKeys<RawStoreState[T][string]>(
            rawEntitiesObject,
            pathList[1],
          ),
        )
        .map(rawEntitiesObject => Object.values(rawEntitiesObject))
        .map(rawEntities => rawEntities.map(rawEntity => rawEntity.entity))
        .distinctUntilChanged(sameElements),
    );
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-i18n-webapp-compat/LANG/de/webapp/src/app/@shared/datastore/datastore.ts